import { ComponentProps, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { Point as MapboxPoint } from 'mapbox-gl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { POISearchResult } from '@alltrails/search/types/algoliaResultTypes';
import useMap from '../../../hooks/useMap';
import { useAppSelector } from '../../../redux';
import { defaultPOIPinLayerPaintProps, getLayerBeforeId, poiPopupPinId, poiPinsId } from '../../../utils/layers';
import { coordinatesToSerializedCoordinates } from '../../../utils/serializedCoordinates';
import { defaultPOIPinXSLayoutProps } from './constants';
import type POIResultProperties from './POIResultProperties';
import POIPopup from './POIPopup';

const poiPopupSourceId = 'poi-popup-source';

type POIPopupPinProps = {
  getPOIUrl?: (poi: POISearchResult) => string;
  onPopupClick?: (poi: POISearchResult) => void;
  onMouseEnter?: (...args: any[]) => void;
  onMouseLeave?: (...args: any[]) => void;
  onPOICardViewed?: ComponentProps<typeof POIPopup>['onPOICardViewed'];
};

const POIPopupPin = ({ getPOIUrl, onPopupClick, onMouseEnter, onMouseLeave, onPOICardViewed }: POIPopupPinProps) => {
  const map = useMap();
  const { hasPermission } = useAuthorization();
  const { hoveredPOI } = useAppSelector(state => ({ hoveredPOI: state.map.hoveredPOI }));
  const hasLocationsManagePermission = hasPermission({ permission: 'locations:manage' });

  const { hoveredFeatures, hoveredClusterCoordinates } = useMemo(() => {
    if (!hoveredPOI?._geoloc) {
      return { hoveredFeatures: [], hoveredClusterCoordinates: undefined };
    }

    // check the map to see if the hovered POI is already rendered
    const renderedFeatures = map?.queryRenderedFeatures({ layers: [poiPinsId] });
    let isRendered = false;
    let hoveredClusterCoordinates = undefined;
    for (const feature of renderedFeatures ?? []) {
      if (feature.properties?.id === hoveredPOI.ID) {
        isRendered = true;
        if (feature.properties?.cluster) {
          hoveredClusterCoordinates = (feature.geometry as MapboxPoint).coordinates;
        }
        break;
      }
    }

    if (isRendered) {
      return { hoveredFeatures: [], hoveredClusterCoordinates };
    }

    // if the POI is not already rendered, render it in a separate source
    return {
      hoveredFeatures: [
        {
          geometry: { type: 'Point', coordinates: [hoveredPOI._geoloc.lng, hoveredPOI._geoloc.lat] },
          properties: {
            hasPhotos: !!hoveredPOI.photos_count,
            id: hoveredPOI.ID,
            name: hoveredPOI.name,
            popularity: hoveredPOI.popularity ?? 0,
            serializedCoordinates: coordinatesToSerializedCoordinates({ lat: hoveredPOI._geoloc.lat, lng: hoveredPOI._geoloc.lng }),
            type: hoveredPOI.subtype
          },
          type: 'Feature'
        }
      ] as Feature<Point, POIResultProperties>[],
      hoveredClusterCoordinates: undefined
    };
  }, [hoveredPOI, map]);

  return (
    <>
      <Source id={poiPopupSourceId} type="geojson" data={{ type: 'FeatureCollection', features: hoveredFeatures }}>
        <Layer
          source={poiPopupSourceId}
          id={poiPopupPinId}
          type="symbol"
          beforeId={getLayerBeforeId(map, poiPopupPinId)}
          layout={{ ...defaultPOIPinXSLayoutProps, 'icon-allow-overlap': true, 'icon-ignore-placement': false }}
          paint={defaultPOIPinLayerPaintProps}
        />
      </Source>
      {hoveredPOI && (
        <POIPopup
          coordinates={hoveredClusterCoordinates}
          getPOIUrl={getPOIUrl}
          onClick={onPopupClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onPOICardViewed={onPOICardViewed}
          poi={hoveredPOI}
          hasLocationsManagePermission={hasLocationsManagePermission}
        />
      )}
    </>
  );
};

export default POIPopupPin;
